import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'

export default function ForgotPassword() {
  const navigate = useNavigate()

  const [step, setStep] = useState(1)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)

  const requestResetCode = async (e) => {
    e?.preventDefault()
    if (!email) return

    setLoading(true)
    setError(null)
    setMessage(null)
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Unable to send reset code.')

      setStep(2)
      setMessage('If an account exists for that email, a reset code was sent.')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (e) => {
    e?.preventDefault()

    if (!email || !code || !newPassword) return
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    setError(null)
    setMessage(null)

    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Unable to reset password.')

      setMessage('Password updated successfully. You can sign in now.')
      setCode('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => navigate('/login', { replace: true }), 900)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-full px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
      <div className="mx-auto w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl lg:grid lg:grid-cols-2">
        <div className="brand-gradient px-6 py-10 sm:px-8 lg:px-10 lg:py-12 text-white">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 text-3xl mb-6">🔐</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight">Reset your password</h1>
          <p className="text-blue-100 mt-3 text-sm sm:text-base">
            Request a secure code and set a new password to regain access to your account.
          </p>

          <div className="mt-8 space-y-3 text-sm text-blue-100/95">
            <p>1. Enter your email to receive a reset code.</p>
            <p>2. Enter the code and choose a new password.</p>
            <p>3. Sign in again with your updated password.</p>
          </div>
        </div>

        <form
          onSubmit={step === 1 ? requestResetCode : resetPassword}
          className="px-6 py-8 sm:px-8 lg:px-10 lg:py-12 flex flex-col gap-5"
        >
          <div className="mb-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
              {step === 1 ? 'Step 1 of 2' : 'Step 2 of 2'}
            </p>
          </div>

          <Input
            label="Email address"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            autoComplete="email"
            disabled={step === 2}
          />

          {step === 2 && (
            <>
              <Input
                label="Reset code"
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="6-digit code"
                required
                maxLength={6}
                autoComplete="one-time-code"
              />
              <Input
                label="New password"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Min. 8 characters"
                required
                autoComplete="new-password"
              />
              <Input
                label="Confirm new password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
                autoComplete="new-password"
              />
            </>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {message && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700">
              {message}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={loading}
            disabled={step === 1 ? !email : !code || !newPassword || !confirmPassword}
          >
            {step === 1 ? 'Send Reset Code' : 'Update Password'}
          </Button>

          {step === 2 && (
            <Button
              type="button"
              variant="ghost"
              size="md"
              fullWidth
              disabled={loading}
              onClick={requestResetCode}
            >
              Resend code
            </Button>
          )}

          <div className="text-center text-sm text-slate-500 pt-1">
            Remembered your password?{' '}
            <Link to="/login" className="text-brand-700 font-semibold hover:underline">
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
